import React from 'react';

export function CommonMistakes() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Common Sourdough Mistakes</h3>
        <p className="mb-4">
          Before diving into specific issues, let's address the four most common mistakes that bakers encounter. Understanding and avoiding these fundamental errors will significantly improve your bread baking results.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">1. Starter Management Issues</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Using Unfed or Weak Starter</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Signs:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Starter doesn't double in size</li>
                  <li>No or few bubbles visible</li>
                  <li>Sour or acetone smell</li>
                  <li>Liquid layer on top (hooch)</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Feed starter 2-3 times before baking</li>
                  <li>Maintain consistent feeding schedule</li>
                  <li>Use starter at peak activity</li>
                  <li>Keep at appropriate temperature</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">2. Temperature Control</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Improper Dough Temperature</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Too Cold:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Slow or stalled fermentation</li>
                  <li>Extended proofing times</li>
                  <li>Dense, heavy crumb</li>
                  <li>Poor flavor development</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Too Warm:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Over-fermentation risk</li>
                  <li>Excessive acidity</li>
                  <li>Weak gluten structure</li>
                  <li>Sticky, difficult dough</li>
                </ul>
              </div>
            </div>
            <div className="mt-4">
              <h5 className="text-amber-400 text-sm">Solutions:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Maintain dough temperature between 75-78°F (24-26°C)</li>
                <li>Use temperature-controlled water</li>
                <li>Consider ambient temperature when planning timeline</li>
                <li>Use proofer or controlled environment if available</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">3. Hydration Issues</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Incorrect Hydration for Flour Type</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Under-hydrated:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Dense, tight crumb</li>
                  <li>Tough crust</li>
                  <li>Poor fermentation</li>
                  <li>Difficult to shape</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Over-hydrated:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Dough too sticky to handle</li>
                  <li>Flat, spread-out loaves</li>
                  <li>Irregular, gummy crumb</li>
                  <li>Poor surface tension</li>
                </ul>
              </div>
            </div>
            <div className="mt-4">
              <h5 className="text-amber-400 text-sm">Solutions:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Match hydration to flour type and protein content</li>
                <li>Start lower and gradually increase hydration</li>
                <li>Consider environmental humidity</li>
                <li>Adjust hydration based on dough feel</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">4. Steam Management</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Inadequate Steam During Baking</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Effects:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Poor oven spring</li>
                  <li>Dull crust appearance</li>
                  <li>Thick, tough crust</li>
                  <li>Uneven expansion</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Use Dutch oven or cloche</li>
                  <li>Create steam with ice/water</li>
                  <li>Ensure proper sealing</li>
                  <li>Remove steam at right time</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Prevention Checklist</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Before Starting</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Ensure starter is active and recently fed</li>
              <li>Check room temperature and adjust timeline</li>
              <li>Verify flour protein content and adjust hydration</li>
              <li>Prepare steam generation method</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">During Process</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Monitor dough temperature</li>
              <li>Watch fermentation progress</li>
              <li>Adjust timing based on dough behavior</li>
              <li>Handle dough gently to preserve structure</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">During Baking</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Preheat oven thoroughly</li>
              <li>Ensure proper steam generation</li>
              <li>Monitor crust development</li>
              <li>Check internal temperature</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}